import { cloudSaveTileAppearance, hasCloudAudioLibrary } from './audioPlayerLibraryApi';

export const DEFAULT_GENRE_TINTS = {
  ambient: '#6b7fd7',
  electronic: '#3aa99a',
  rock: '#c9863f',
  jazz: '#a65d7a',
  folk: '#6b9340',
  classical: '#5c7a9e',
  playlist: '#b87333',
};

const APPEARANCE_STORAGE_KEY = 'bahlus-audio-tile-appearance';

/** When set, signed-in account tile appearances (not localStorage). */
let cloudAppearanceMap = null;

export function clearCloudTileAppearanceCache() {
  cloudAppearanceMap = null;
}

export function sanitizeTileColor(value) {
  if (value == null || value === '') return null;
  let v = String(value).trim();
  if (!v.startsWith('#')) v = `#${v}`;
  if (/^#[0-9a-fA-F]{3}$/.test(v)) {
    const r = v[1];
    const g = v[2];
    const b = v[3];
    v = `#${r}${r}${g}${g}${b}${b}`;
  }
  if (!/^#[0-9a-fA-F]{6}$/.test(v)) return null;
  return v.toLowerCase();
}

export function sanitizeTileLabel(value, maxLen = 120) {
  const t = String(value || '').trim();
  if (!t) return null;
  return t.slice(0, maxLen);
}

export function resolveTileColor(customColor, genreId) {
  return sanitizeTileColor(customColor) || DEFAULT_GENRE_TINTS[genreId] || DEFAULT_GENRE_TINTS.playlist;
}

function hexToRgb(hex) {
  const h = sanitizeTileColor(hex);
  if (!h) return { r: 184, g: 115, b: 51 };
  return {
    r: parseInt(h.slice(1, 3), 16),
    g: parseInt(h.slice(3, 5), 16),
    b: parseInt(h.slice(5, 7), 16),
  };
}

/** Inline style for translucent “glass” tiles (Apple-style). */
export function getTileSurfaceStyle(hex) {
  const color = resolveTileColor(hex);
  const { r, g, b } = hexToRgb(color);
  return {
    '--apl-genre-tint': color,
    '--apl-tile-fill': `rgba(${r}, ${g}, ${b}, 0.16)`,
    '--apl-tile-fill-hover': `rgba(${r}, ${g}, ${b}, 0.26)`,
    '--apl-tile-stroke': `rgba(${r}, ${g}, ${b}, 0.28)`,
    '--apl-tile-icon-bg': `rgba(${r}, ${g}, ${b}, 0.22)`,
    '--apl-tile-badge-bg': `rgba(${r}, ${g}, ${b}, 0.2)`,
  };
}

function readTileAppearanceMapLocal() {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = JSON.parse(localStorage.getItem(APPEARANCE_STORAGE_KEY) || '{}');
    return raw && typeof raw === 'object' ? raw : {};
  } catch {
    return {};
  }
}

function writeTileAppearanceMapLocal(map) {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(APPEARANCE_STORAGE_KEY, JSON.stringify(map || {}));
}

export function readTileAppearanceMap() {
  if (cloudAppearanceMap !== null) return cloudAppearanceMap;
  return readTileAppearanceMapLocal();
}

/** Guest-only local map (for migrating to account on first sign-in). */
export function readTileAppearanceMapLocalOnly() {
  return readTileAppearanceMapLocal();
}

export function writeTileAppearanceMap(map) {
  if (cloudAppearanceMap !== null) {
    cloudAppearanceMap = map || {};
    return;
  }
  writeTileAppearanceMapLocal(map);
}

export function catalogStreamAppearanceKey(genreId, streamId) {
  return `catalog:${genreId}:${streamId}`;
}

export function builtinGenreAppearanceKey(genreId) {
  return `builtin-genre:${genreId}`;
}

export function sectionAppearanceKey(sectionKey) {
  return `section:${sectionKey}`;
}

export function getLocalTileAppearance(key) {
  if (!key) return {};
  const map = readTileAppearanceMap();
  const row = map[key];
  if (!row || typeof row !== 'object') return {};
  return {
    tileLabel: row.tileLabel != null ? String(row.tileLabel) : '',
    tileColor: sanitizeTileColor(row.tileColor),
  };
}

export function setLocalTileAppearance(key, { tileLabel, tileColor }) {
  if (!key) return;
  const map = { ...readTileAppearanceMap() };
  const label = sanitizeTileLabel(tileLabel);
  const color = sanitizeTileColor(tileColor);
  if (!label && !color) {
    delete map[key];
  } else {
    map[key] = {
      ...(label ? { tileLabel: label } : {}),
      ...(color ? { tileColor: color } : {}),
    };
  }
  writeTileAppearanceMap(map);
}

export function appearanceRowsToMap(rows) {
  const map = {};
  (rows || []).forEach((row) => {
    if (!row?.key) return;
    const label = sanitizeTileLabel(row.tileLabel);
    const color = sanitizeTileColor(row.tileColor);
    if (!label && !color) return;
    map[row.key] = {
      ...(label ? { tileLabel: label } : {}),
      ...(color ? { tileColor: color } : {}),
    };
  });
  return map;
}

/** Apply account tile appearances after sync (signed-in; no localStorage). */
export function applySyncedTileAppearances(rows) {
  cloudAppearanceMap = appearanceRowsToMap(rows);
}

/** Guest: localStorage only. Signed in: account API + in-memory cache. */
export async function saveTileAppearance(key, { tileLabel, tileColor } = {}) {
  if (!key) return null;
  const label = sanitizeTileLabel(tileLabel);
  const color = sanitizeTileColor(tileColor);

  if (await hasCloudAudioLibrary()) {
    const saved = await cloudSaveTileAppearance({
      key: String(key),
      tileLabel: label,
      tileColor: color,
    });
    if (cloudAppearanceMap === null) {
      cloudAppearanceMap = {};
    }
    setLocalTileAppearance(key, {
      tileLabel: saved?.tileLabel,
      tileColor: saved?.tileColor,
    });
    return saved;
  }

  setLocalTileAppearance(key, { tileLabel: label, tileColor: color });
  return { key: String(key), tileLabel: label, tileColor: color };
}

export function tileDisplayTitle(defaultTitle, tileLabel) {
  const custom = sanitizeTileLabel(tileLabel);
  return custom || String(defaultTitle || '').trim() || 'Untitled';
}

export function tileLabelForSave(tileLabel, defaultTitle) {
  const custom = sanitizeTileLabel(tileLabel);
  const base = String(defaultTitle || '').trim();
  if (!custom || custom === base) return null;
  return custom;
}
